/**
 * File System API
 * Handles file/folder operations via sidecar
 */

import { apiRequest, apiDownload, apiUpload, apiPostForBlob, apiReadBlob } from './client.js';

/**
 * List directory contents
 * @param {string} path - Directory path
 * @returns {Promise<Array>} Array of file/folder objects
 */
export async function listDirectory(path) {
    return apiRequest(`/api/fs/list?path=${encodeURIComponent(path)}`);
}

/**
 * Read file content inline (v0.13 /fs/read)
 * Streams file with detected MIME type, no download header.
 * @param {string} path - Absolute file path
 * @returns {Promise<string>} File content as text
 */
export async function readFile(path) {
    return apiReadBlob(path);
}

/**
 * Write file content (v0.13 PUT /fs/write)
 * Raw body written to disk; parent dirs created automatically.
 * @param {string} path - Absolute file path
 * @param {string|Blob} content - Content to write
 * @returns {Promise<any>} { success, path, size }
 */
export async function writeFile(path, content) {
    const { getToken } = await import('../utils/storage.js');
    const { getActiveMachine, getMachineBaseURL } = await import('../utils/config.js');
    const machine = getActiveMachine();
    const token = getToken(machine?.name || 'default');
    const baseURL = getMachineBaseURL(machine);
    const url = `${baseURL}/api/fs/write?path=${encodeURIComponent(path)}`;
    const response = await fetch(url, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'text/plain',
        },
        body: content,
    });
    if (!response.ok) {
        throw new Error(`Write failed: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Download single file or folder (as ZIP)
 * @param {string} path - File/folder path
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Blob>} File blob
 */
export async function downloadFile(path, onProgress = null) {
    return apiDownload(path, onProgress);
}

/**
 * Download multiple files as ZIP
 * @param {Array<string>} paths - Array of file/folder paths
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Blob>} ZIP blob
 */
export async function downloadBatch(paths, onProgress = null) {
    return apiPostForBlob('/api/fs/download-batch', paths, onProgress);
}

/**
 * Upload files to directory
 * @param {string} targetPath - Target directory
 * @param {FileList} files - Files to upload
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<any>} Upload result
 */
export async function uploadFiles(targetPath, files, onProgress) {
    return apiUpload(targetPath, files, onProgress);
}

/**
 * Create new directory
 * @param {string} path - Directory path to create
 * @returns {Promise<any>} Result
 */
export async function createDirectory(path) {
    return apiRequest('/api/fs/mkdir', {
        method: 'POST',
        body: JSON.stringify({ path }),
    });
}

/**
 * Rename/move file or directory
 * @param {string} oldPath - Current path
 * @param {string} newPath - New path
 * @returns {Promise<any>} Result
 */
export async function renameItem(oldPath, newPath) {
    return apiRequest('/api/fs/rename', {
        method: 'POST',
        body: JSON.stringify({ from: oldPath, to: newPath }),
    });
}

/**
 * Copy file or directory
 * @param {string} sourcePath - Source path
 * @param {string} destPath - Destination path
 * @returns {Promise<any>} Result
 */
export async function copyItem(sourcePath, destPath) {
    return apiRequest('/api/fs/copy', {
        method: 'POST',
        body: JSON.stringify({ from: sourcePath, to: destPath }),
    });
}

/**
 * Delete file or directory
 * @param {string} path - Path to delete
 * @returns {Promise<any>} Result
 */
export async function deleteItem(path) {
    return apiRequest('/api/fs/remove', {
        method: 'DELETE',
        body: JSON.stringify({ path }),
    });
}

/**
 * Search files and directories by glob pattern
 * @param {string} path - Root directory to search
 * @param {string} pattern - Glob pattern to match
 * @param {number} [maxDepth] - Max depth (server default: 10, max: 50)
 * @returns {Promise<{matches: Array, total: number}>} Search results
 */
export async function searchFiles(path, pattern, maxDepth) {
    let url = `/api/fs/search?path=${encodeURIComponent(path)}&pattern=${encodeURIComponent(pattern)}`;
    if (maxDepth != null) url += `&max_depth=${maxDepth}`;
    return apiRequest(url);
}

/**
 * Delete multiple items
 * @param {Array<string>} paths - Paths to delete
 * @returns {Promise<Array>} Results
 */
export async function deleteBatch(paths) {
    const results = [];
    for (const path of paths) {
        try {
            await deleteItem(path);
            results.push({ path, success: true });
        } catch (error) {
            results.push({ path, success: false, error: error.message });
        }
    }
    return results;
}
